import { z } from 'zod';
/**
 * The newsletter's small vocabulary — `docs/lovespa-gaps-plan.md` E3.
 *
 * It is a file of its own rather than three more exports in `messaging.ts`
 * because the two channels answer to different rules. SMS consent is a TCPA
 * question about a *telephone number*; email marketing consent is a CAN-SPAM
 * question about an *address*, and CAN-SPAM famously does not require prior
 * consent at all — what it requires is a truthful sender, a physical address
 * and a working unsubscribe. This platform asks for consent anyway, because
 * deliverability is decided by recipients rather than by statutes and a list
 * nobody asked to be on is a list that gets a sending domain blocked.
 */
/**
 * How a client came to be on the newsletter — the closed set behind
 * `clients.email_marketing_opt_in_source`.
 *
 * It is `SMS_OPT_IN_SOURCES`' sibling and deliberately not the same list, in
 * both directions:
 *
 *  - `staff`        — the tick on the client record, at the counter. The
 *                     commonest and the one the owner will actually use.
 *  - `guest_site`   — the newsletter band, when the address already belongs to
 *                     a client. (A *stranger's* submit creates a
 *                     `newsletter_subscriptions` row and touches no client at
 *                     all; migration 0032 §4 argues why.)
 *  - `unsubscribe`  — they used the link. Recorded as a source because the
 *                     column describes the last change in *either* direction,
 *                     and the withdrawal is the half with a regulator attached.
 *  - `bounce`       — the address is dead, so the flag came off by itself.
 *                     Distinct from `unsubscribe` because nobody asked: it is
 *                     hygiene, and an owner reading the record should be able
 *                     to tell "she left" from "her mailbox is gone".
 *  - `import_none`  — the flag arrived from an import with **no provenance**.
 *                     A value rather than `NULL`, for `SMS_OPT_IN_SOURCES`'
 *                     reason. Note that no importer in this repo writes it:
 *                     imported contacts are **not** opted in (migration 0042),
 *                     and the name exists for the day somebody imports a list
 *                     that genuinely carries a consent column.
 *
 * There is no `reply_yes`: email has no keyword channel, and there is no
 * `partner`, for §2.6's reason — a tenant whose partner owns guest messaging
 * has the partner as the authority on consent, so a row recorded here would be
 * a second answer to a question that is not ours.
 *
 *  - `anonymized`   — the flag went to `false` because the guest exercised the
 *                     right to be forgotten (§7.6, `anonymizeClient`). The same
 *                     value `SMS_OPT_IN_SOURCES` carries, for the same reason:
 *                     it is the one provenance that also explains why the rest
 *                     of the row is blank. Migration 0061 widens the CHECK.
 */
export const EMAIL_OPT_IN_SOURCES = [
    'staff',
    'guest_site',
    'unsubscribe',
    'bounce',
    'import_none',
    'anonymized',
];
export const EmailOptInSource = z.enum(EMAIL_OPT_IN_SOURCES);
/**
 * A tenant's newsletter settings, in `tenant_config.config.newsletter`.
 *
 * All of it optional and all of it absent for every tenant today, which is the
 * convention every config block added since A2 follows: absent must be
 * byte-identical to the behaviour before the block existed. An absent block
 * means the tenant can still compose and preview — the composer needs nothing
 * from here — and that a send falls back to the derived from-address and to the
 * address published in the site's own `location` section.
 */
export const NewsletterConfig = z.object({
    /**
     * The local part of the From address: `lovespa` in
     * `lovespa@mail.example.com`. **Never** typed by a guest or carried on a
     * request — a From address is an identity claim on a domain the platform
     * owns, so this is an operator/owner setting and `defaultLocalPart` derives
     * one from the tenant id when it is unset.
     */
    from_local_part: z
        .string()
        .regex(/^[a-z0-9](?:[a-z0-9._-]{0,46}[a-z0-9])?$/, 'lower-case letters, digits, . _ -')
        .optional(),
    /**
     * Where a reply goes. The tenant's *own* mailbox, because a customer who
     * answers a newsletter is answering the business and not the platform — and
     * because a From address on our sending subdomain has no inbox behind it at
     * all. Absent means replies go nowhere, which is honest but poor, so the
     * composer says so.
     */
    reply_to: z.string().max(320).optional(),
    /**
     * The postal address the footer carries, when it should differ from the one
     * published on the website — a mailbox rather than a treatment room, which
     * is a reasonable thing for a sole proprietor to want. Absent falls back to
     * the site's `location` block, and when *both* are absent the send is
     * refused rather than sent without one.
     */
    postal_address: z.string().max(400).optional(),
    /**
     * Messages per hour for this tenant. A courtesy to the sending domain's
     * warm-up rather than a limit anybody has hit: a new domain that emits four
     * hundred messages in its first minute is a new domain that gets filtered.
     * Absent means the platform default.
     */
    rate_per_hour: z.number().int().min(1).max(100_000).optional(),
    /** The sentence in the footer saying why this person is receiving it. */
    reason: z.string().max(300).optional(),
});
/**
 * Read the block out of a `tenant_config.config` document that was **cast
 * rather than parsed** — `orderTimingOf`'s pattern, for its reason: the
 * document is stored as JSONB and read back as `unknown`, and a reader that
 * assumed its own shape would be the place a hand-edited row becomes a crash.
 */
export function newsletterConfigOf(config) {
    if (config === null || typeof config !== 'object' || Array.isArray(config))
        return {};
    const raw = config['newsletter'];
    const parsed = NewsletterConfig.safeParse(raw);
    return parsed.success ? parsed.data : {};
}
//# sourceMappingURL=newsletter.js.map